import { Box, Button, IconButton, Link, SvgIcon, Tooltip, Typography } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { LineItem } from 'sr-types/lib/shortlist/v1/graphql';
import { UserContext } from '../common/auth/UserContext';
import EllipsisText from '../common/components/EllipsisText';
import LineClamp from '../common/components/LineClamp';
import Loading from '../common/components/Loading';
import EngagementTracker from '../common/components/person/engagementTracker/EngagementTracker';
import { PROFESSIONAL_ROLES_ID, useGetProfessionalExperience } from '../common/components/person/person';
import { findAlternateId } from '../common/components/utils/alternateIdFormUtils';
import { IMDB_LABEL, LINKEDIN_LABEL, STUDIO_SYSTEM_LABEL } from '../common/constants/alternateIdConstants';
import I18n from '../common/i18n/I18n';
import { Icons } from '../common/icons/Icons';
import { C, H, V } from '../common/Layout';
import { ImageContainer } from '../common/list/dataListComponents/ImageContainer';
import MembershipsSection from '../common/list/dataListComponents/MembershipsSection';
import RolesAndTypesSection from '../common/list/dataListComponents/RolesAndTypesSection';
import useShowResumeIconByApp from '../common/list/dataListComponents/useShowResumeIconByApp';
import {
    transformLocationToDisplay,
    transformNameToDisplay,
    transformSummaryToDisplay
} from '../common/utils/commonUtils';
import { removeEmptyExperiences } from '../person/detailsPage/detailsPageUtilities/personBasicInformation';
import ChipsPreview from '../person/detailsPage/editComponents/ChipsPreview';
import { ResumeIcon } from '../person/detailsPage/personResume/PersonHeaderResume';
import UploadResume from '../person/detailsPage/personResume/UploadResume';
import { AboutSection } from '../person/detailsPage/personSpecifications/AboutSection';
import { getMostRecentExperience } from '../production/helpers/peopleUtils';
import { changeUrl } from '../shortlists/shortlistLineItem';
import CreditsAndExperienceMarketplace from './CreditsAndExperienceMarketplace';

interface MarketplaceProfileDetailsProps {
    item?: any;
    shortlistLineItems?: { current: string[] };
    onAddToShortlist?: (item: LineItem) => void | Promise<unknown>;
    isOrganizationDetails?: boolean;
    onSeeMore?: () => {};
    isProfilePreview?: boolean;
    showAddToShortlistButton?: boolean;
}

export const MarketplaceProfileDetails: React.FC<MarketplaceProfileDetailsProps> = ({
    item,
    shortlistLineItems = { current: [] },
    onAddToShortlist = undefined,
    isOrganizationDetails = false,
    onSeeMore = undefined,
    isProfilePreview,
    showAddToShortlistButton = true
}) => {
    const { application } = useContext(UserContext);
    const [isAddingToShortlist, setIsAddingToShortlist] = useState(false);
    const [pendingItemRef, setPendingItemRef] = useState<string | null>(null);

    const { experienceDetails, experienceDetailsLoading } = useGetProfessionalExperience(
        item?.id,
        isOrganizationDetails || !item?.id
    );

    const showResumeIconByApp = useShowResumeIconByApp(item);

    const imageId = useMemo(() => {
        if (item?.profileImage) {
            const profileImagePath = item.profileImage.split('/');
            return profileImagePath.pop();
        }
        return item?.branding?.bigLogo?.id;
    }, [item]);

    const buttonDisabled = useMemo(() => {
        return shortlistLineItems.current.includes(item?.reference?.id);
    }, [shortlistLineItems.current, item?.reference?.id]);

    useEffect(() => {
        if (pendingItemRef && buttonDisabled && isAddingToShortlist) {
            setIsAddingToShortlist(false);
            setPendingItemRef(null);
        }
    }, [buttonDisabled, isAddingToShortlist, pendingItemRef]);

    const handleAddToShortlistClick = () => {
        setIsAddingToShortlist(true);
        const targetItemRef = item?.reference?.id || item?.id || null;
        setPendingItemRef(targetItemRef);

        Promise.resolve(onAddToShortlist(item))
            .then(() => {
                if (!targetItemRef) {
                    setIsAddingToShortlist(false);
                    setPendingItemRef(null);
                }
            })
            .catch(() => {
                setIsAddingToShortlist(false);
                setPendingItemRef(null);
            });
    };

    const experiences = experienceDetails?.person?.experiences || item?.experiences;
    const filteredExperiences = experiences && removeEmptyExperiences(experiences);

    const displayName = useMemo(() => transformNameToDisplay(item?.name), [item?.name]);
    const displayLocation = useMemo(() => transformLocationToDisplay(item?.location), [item?.location]);
    const { displayAbout, displayHeadline } = useMemo(() => transformSummaryToDisplay(item?.summary), [item?.summary]);

    const IMDbLink = (item?.alternateIds?.length && findAlternateId(item?.alternateIds, IMDB_LABEL)) || item?.Imdb;

    const linkedinLink =
        (item?.alternateIds?.length && findAlternateId(item?.alternateIds, LINKEDIN_LABEL)) || item?.LinkedIn;

    const studioSystemLink =
        (item?.alternateIds?.length && findAlternateId(item?.alternateIds, STUDIO_SYSTEM_LABEL)) || item?.StudioSystem;

    const openURLInNewTab = (url: string) => {
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        if (newWindow) {
            newWindow.opener = null;
        }
    };

    const mostRecentExperience = getMostRecentExperience(filteredExperiences);
    const experienceNameToShow = mostRecentExperience?.experienceName || item?.organization;

    return !item ? (
        <Loading />
    ) : (
        <V sx={{ alignItems: 'flex-start', height: '100%', overflow: 'auto' }} gap={2}>
            <V
                sx={{
                    gap: 1.5,
                    alignItems: 'flex-start',
                    width: '100%',
                    py: 1,
                    border: isProfilePreview && 1,
                    borderColor: isProfilePreview ? 'divider' : 'transparent',
                    borderRadius: 1,
                    p: isProfilePreview && 2
                }}
            >
                <H gap={2}>
                    <ImageContainer
                        name={displayName}
                        imageId={imageId}
                        customImageSize="66px"
                        customInitialsFontSize="30px"
                    />

                    <V sx={{ gap: 1 }}>
                        <H sx={{ fontSize: 'large', width: 'fit-content', gap: 1 }}>
                            <Link
                                sx={{
                                    color: 'text.primary',
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                        color: 'primary.main'
                                    },
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                                onClick={
                                    isProfilePreview
                                        ? () => changeUrl(application, isOrganizationDetails, item)
                                        : onSeeMore
                                }
                            >
                                <Typography fontSize={20} fontWeight="semibold" sx={{ cursor: 'pointer' }}>
                                    {displayName}
                                </Typography>
                                {item.isSharedInDirectory && <Icons.ShowrunnrMark />}
                            </Link>
                        </H>
                        <H gap={1}>
                            {showAddToShortlistButton && (
                                <Tooltip
                                    title={
                                        <I18n
                                            token={
                                                buttonDisabled
                                                    ? 'menu.create.shortlist.already'
                                                    : 'menu.create.shortlist'
                                            }
                                        />
                                    }
                                >
                                    <Box>
                                        <IconButton
                                            size="small"
                                            disabled={buttonDisabled || isAddingToShortlist || !onAddToShortlist}
                                            onClick={handleAddToShortlistClick}
                                        >
                                            {isAddingToShortlist ? (
                                                <Loading size="21px" />
                                            ) : (
                                                <Icons.InquiryIcon
                                                    sx={{
                                                        color: buttonDisabled ? 'text.disabled' : 'text.primary'
                                                    }}
                                                />
                                            )}
                                        </IconButton>
                                    </Box>
                                </Tooltip>
                            )}
                        </H>
                    </V>
                </H>
                <V gap={1}>
                    {(() => {
                        const headlineToDisplay = displayHeadline !== '' ? displayHeadline : (item.headline ?? '');
                        return headlineToDisplay ? <LineClamp lineClamp={2} text={headlineToDisplay} /> : null;
                    })()}
                    {!isOrganizationDetails && !experienceDetailsLoading && experienceNameToShow && (
                        <H gap={1} sx={{ color: 'text.primary' }}>
                            <Icons.Organization fontSize="medium" />
                            <EllipsisText text={experienceNameToShow} />
                        </H>
                    )}

                    {isOrganizationDetails && (
                        <H gap={1} sx={{ color: 'text.primary' }}>
                            <Icons.Language fontSize="medium" />
                            <Link
                                sx={{
                                    color: 'text.primary',
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                                href={item.websiteURL}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {item.websiteURL}
                            </Link>
                        </H>
                    )}

                    <H gap={1} sx={{ color: 'text.primary' }}>
                        <Icons.LocationOutlined fontSize="medium" />
                        <EllipsisText text={displayLocation} />
                    </H>

                    {(item.hierarchicalCategories?.map((category) => ({ value: [category.value] })) ||
                        item.customFields) && (
                        <ChipsPreview
                            customFields={
                                item.hierarchicalCategories?.map((category) => ({ value: [category.value] })) ||
                                item.customFields
                            }
                            size="small"
                        />
                    )}

                    {isProfilePreview && (
                        <H gap={0.5}>
                            {(linkedinLink || IMDbLink || studioSystemLink) && (
                                <>
                                    {linkedinLink && (
                                        <SvgIcon
                                            component={Icons.LinkedIn}
                                            sx={{ color: 'text.secondary', cursor: 'pointer' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openURLInNewTab(linkedinLink?.url || linkedinLink);
                                            }}
                                        />
                                    )}
                                    {IMDbLink && (
                                        <SvgIcon
                                            component={Icons.IMDb}
                                            sx={{ color: 'text.secondary', cursor: 'pointer' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openURLInNewTab(IMDbLink?.url || IMDbLink);
                                            }}
                                        />
                                    )}
                                    {studioSystemLink && (
                                        <Tooltip title={<I18n token="common.studio.system" />}>
                                            <SvgIcon
                                                component={Icons.Link}
                                                sx={{ color: 'text.secondary', cursor: 'pointer' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openURLInNewTab(studioSystemLink.url || studioSystemLink);
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </>
                            )}
                            {showResumeIconByApp && item.resumeReference && (
                                <UploadResume FileRenderer={ResumeIcon} state={item} />
                            )}
                        </H>
                    )}
                    {isProfilePreview && !isOrganizationDetails && (
                        <V gap={1}>
                            <RolesAndTypesSection
                                id={PROFESSIONAL_ROLES_ID}
                                professionalRoles={item.professionalRoles}
                                organizationTypes={item.organizationTypes}
                                editEnabled={false}
                            />
                            <MembershipsSection memberships={item.memberships} editEnabled={false} />
                        </V>
                    )}

                    {!isProfilePreview && (
                        <Button onClick={onSeeMore}>
                            <I18n token="common.label.more" />
                        </Button>
                    )}
                </V>
            </V>

            {isProfilePreview && (
                <C
                    sx={{
                        width: '100%',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 2
                    }}
                >
                    {/* Prefer summary-derived displayAbout; fall back to legacy item.about; default to empty string. */}
                    <AboutSection about={displayAbout ?? item.about ?? ''} />
                </C>
            )}
            {isProfilePreview && !isOrganizationDetails && (
                <>
                    <C
                        sx={{
                            width: '100%',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            maxHeight: 420,
                            minHeight: 260
                        }}
                    >
                        <EngagementTracker personId={item?.reference?.id} />
                    </C>
                    <CreditsAndExperienceMarketplace item={item} isOrganizationDetails={isOrganizationDetails} />
                </>
            )}
        </V>
    );
};
